/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, SEC_LEVEL_LIST} from '../../../../common/constants';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {LoadingCounter} from '../../../../core/helpers/loading-counter';
import {StateService} from '../../../../core/state/state.service';
import {WhiteSpaceInputValidator} from '../../../../core/validators/white-space-input-validator';
import {FcorgRegionMapModel} from '../../../../models/fcorg-region-map.model';
import {Fcorg} from '../../../../models/fcorg.model';
import {GfsUserDtoModel} from '../../../../models/gfs-user-dto.model';
import {User} from '../../../../models/user.model';
import {FcorgService} from '../../../../services/fcorg.service';
import {GfsUserService} from '../../../../services/gfs-user.service';
import {RegionService} from '../../../../services/region.service';

@Component({
    selector: 'app-new-user',
    templateUrl: './add-user.component.html',
    styleUrls: ['./add-user.component.scss'],
    standalone: false
})
export class AddUserComponent implements OnInit {
  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  readonly loading = new LoadingCounter();
  userForm: FormGroup;
  newGfsUser: GfsUserDtoModel;
  user: User;

  // displayedColumns: string[] = ['fcorg', 'regions'];
  selectedFcorgs: string[];
  dataSource: MatTableDataSource<FcorgRegionMapModel> = new MatTableDataSource<FcorgRegionMapModel>([]);
  regionMap: FcorgRegionMapModel[] = [];

  fcorgList: Fcorg[];
  regionList: string[];
  regionsToSubmit: string[] = [];
  fcorgsToSubmit: string[] = [];

  fcorgRegionMapColumns: ColDef[] = [
    {field: 'fcorg', headerName: 'Global FCORG', filter: AutocompleteFilterComponent},
    {field: 'region', headerName: 'Region'}
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  secList = SEC_LEVEL_LIST;

  constructor(private readonly userService: StateService,
              readonly snackbar: FabMessageService,
              readonly router: Router,
              private readonly gfsUserService: GfsUserService,
              private readonly fcorgService: FcorgService,
              private readonly regionService: RegionService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.loadFcorgs();
    this.loadRegions();
    this.userForm = new FormGroup({
      id: new FormControl(this.newGfsUser, [Validators.required, WhiteSpaceInputValidator.noWhitespaceValidator]),
      firstName: new FormControl(this.newGfsUser, [Validators.required, WhiteSpaceInputValidator.noWhitespaceValidator]),
      lastName: new FormControl(this.newGfsUser, [Validators.required, WhiteSpaceInputValidator.noWhitespaceValidator]),
      role: new FormControl(this.newGfsUser, Validators.required),
      fcorgs: new FormControl(this.newGfsUser, Validators.required),
      regions: new FormControl(this.newGfsUser, Validators.required)
    });
  }

  /**
   * Creates a userDTO object from user input and call backend endpoint to generate
   * new user.
   */
  onSubmit(): void {
    // loop through regionMap and push final regions/fcorgs
    this.regionMap.forEach(user => {
      this.fcorgsToSubmit.push(user.fcorg);
      this.regionsToSubmit.push(user.region);
    });

    this.newGfsUser = {
      id: this.userForm.get('id').value,
      firstName: this.userForm.get('firstName').value,
      lastName: this.userForm.get('lastName').value,
      role: this.userForm.get('role').value,
      fcorgs: this.fcorgsToSubmit,
      regions: this.regionsToSubmit,
      domain: 'ND'
    };

    this.loading.track(this.gfsUserService.addUser(this.newGfsUser)).subscribe({
      error: err => {
        this.snackbar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.snackbar.displaySuccess('Successfully created user', DISMISS_MSG);
        this.onCancel();
      }
    });
  }

  /**
   * Fetches the Fcorgs from the user service and sets data for the drop down
   */
  loadFcorgs(): void {
    this.loading.track(this.fcorgService.getAllFcorgs()).subscribe(
      (data) => {
        this.fcorgList = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Fetches the Regions from the user service and sets data for the drop down
   */
  loadRegions(): void {
    this.loading.track(this.regionService.getAllRegions()).subscribe(
      (data) => {
        this.regionList = data;
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Returns a FcorgRegionMap value based off what the user selects in the region select option
   * @param region - region value
   * @param fcorg - fcorg value
   */
  getRegionMapValue(region: string, fcorg: string): FcorgRegionMapModel {
    let mapValue = this.regionMap.find(map => map.region === region && map.fcorg === fcorg);
    if (mapValue === undefined) {
      mapValue = new FcorgRegionMapModel(null, null, region, fcorg, null);
    }
    return mapValue;
  }

  /**
   * updates regionMap and table if a fcorg is de-selected
   */
  fcorgChange(event): void {
    // If an FCORG is de-selected
    if (!event.source.selected) {
      this.regionMap = this.regionMap.filter(regionMap => regionMap.fcorg !== event.source.value);
    }
  }

  /**
   * Cancel button navigates back to user maintenance page
   */
  onCancel(): void {
    this.router.navigate(['/admin/user-maintenance']);
  }
}
